import {
  faBootHeeled,
  faUserPlus,
  faXmark,
  faArrowDownFromLine,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Invitation } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { InviteButton } from '../components/SendInvitePanel';
import { IdentityPlatform, InvitationSendState } from '@playcastdotio/CoreTypes';
import { faDiscord, faTwitch } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '@clerk/clerk-react';
import { Input } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import {
  sendAnalytics,
  PlaycastAnalyticsInviteFriendsInitialized,
  PlaycastAnalyticsBroadcastLaunched,
  PlaycastAnalyticsInviteCreated,
} from '@playcastdotio/Analytics';

type ModalProps = {
  onClose: () => void;
  onDestroy: () => void;
  authId: string;
  ownerAliases?: Partial<Record<IdentityPlatform, string>> | null;
  selectedInvitationId: string | null | undefined;
  invitations: Invitation[];
  onJoin?: (playerName: string) => void;
  onLeave?: () => void;
  onStart?: (useSteamGuestLogin?: boolean, regionOverride?: string) => void;
  onKickPlayer?: (playerId: string) => void;
  inQueue?: string;
  matchFoundId?: string;
  matchBroadcastId?: string;
  invitationSendState: InvitationSendState;
  handleSendEmail: (invitationId: string, target: string, message: string) => void;
  handleSendSms: (invitationId: string, target: string, message: string) => void;
  handleTwitchBot: (invitationId: string) => void;
  handleDiscordBot: (invitationId: string) => void;
  isVisible?: boolean;
  onShow?: () => void;
  onRemoveSelectedInvitation: () => void;
  initialPosition?: { x: number; y: number } | null;
  onUpdatePlayerName: (name: string) => void;
  persistentId?: string;
  parentType?: string;
};

export const LauncherModal = ({
  onClose,
  onDestroy,
  authId,
  selectedInvitationId,
  invitations,
  ownerAliases,
  onJoin,
  onLeave,
  onStart,
  onKickPlayer,
  inQueue,
  matchFoundId,
  matchBroadcastId,
  invitationSendState,
  handleSendEmail,
  handleSendSms,
  handleTwitchBot,
  handleDiscordBot,
  isVisible = true,
  onShow,
  onRemoveSelectedInvitation,
  initialPosition,
  onUpdatePlayerName,
  persistentId,
  parentType,
}: ModalProps) => {
  const { isSignedIn } = useAuth();
  const [showLauncher, setShowLauncher] = useState(isVisible);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [lastLaunchTime, setLastLaunchTime] = useState<number>(0);
  const [cooldownActive, setCooldownActive] = useState<boolean>(false);
  const [joiningMatchedBroadcast, setJoiningMatchedBroadcast] = useState<boolean>(false);
  // const [showLoginDialog, setShowLoginDialog] = useState<boolean>(false);
  const [loginPreferences, setLoginPreferences] = useState<Record<string, boolean>>({});
  const [queueStartTime, setQueueStartTime] = useState<number | null>(null);
  const [queueFallbackTriggered, setQueueFallbackTriggered] = useState<boolean>(false);
  const [playerName, setPlayerName] = useState<string>('');
  const [joinButtonClicked, setJoinButtonClicked] = useState<boolean>(false);
  const [launchButtonClicked, setLaunchButtonClicked] = useState<boolean>(false);

  // Draggable state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

  const previousPlayersRef = useRef<Array<{ playerId?: string; displayName?: string; preferences?: Record<string, never>; pingMap?: Record<string, number> }>>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const LAUNCH_COOLDOWN = 5000; // 5 seconds cooldown
  const QUEUE_FALLBACK_TIMEOUT = 2 * 60 * 1000; // 2 minutes

  // Initialize player name from localStorage
  useEffect(() => {
    const storedName = localStorage.getItem('name') || '';
    setPlayerName(storedName);
  }, []);

  // Draggable handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!modalRef.current) return;

    // Calculate offset as the difference between mouse position and modal position
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    });
    setIsDragging(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!modalRef.current) return;

    const touch = e.touches[0];
    if (!touch) return;

    // Prevent default touch behavior to stop page scrolling
    e.preventDefault();
    e.stopPropagation();

    // Calculate offset as the difference between touch position and modal position
    setDragOffset({
      x: touch.clientX - modalPosition.x,
      y: touch.clientY - modalPosition.y,
    });
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    // Calculate new position by subtracting the offset from current mouse position
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep modal within viewport bounds
    const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 0);

    setModalPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, dragOffset]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;

    // Prevent default touch behavior to stop page scrolling
    e.preventDefault();

    const touch = e.touches[0];
    if (!touch) return;

    // Calculate new position by subtracting the offset from current touch position
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;

    // Keep modal within viewport bounds
    const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 0);

    setModalPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, dragOffset]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove]);

  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none';
      return () => {
        document.body.style.userSelect = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isDragging]);

  useEffect(() => {
    if (selectedInvitationId) {
      if (!(selectedInvitationId in loginPreferences)) {
        setLoginPreferences((prev) => ({
          ...prev,
          [selectedInvitationId]: true,
        }));
      }
    }
  }, [selectedInvitationId, loginPreferences]);

  useEffect(() => {
    if (lastLaunchTime > 0) {
      setCooldownActive(true);
      const timerId = setTimeout(() => {
        setCooldownActive(false);
      }, LAUNCH_COOLDOWN);

      return () => clearTimeout(timerId);
    }
  }, [lastLaunchTime, LAUNCH_COOLDOWN]);

  // Track queue state and handle fallback timeout
  useEffect(() => {
    const isCurrentlyInQueue = inQueue === selectedInvitationId;
    const wasInQueue = queueStartTime !== null;

    if (isCurrentlyInQueue && !wasInQueue && !queueFallbackTriggered) {
      // Just entered queue, start tracking time
      setQueueStartTime(Date.now());
    } else if (!isCurrentlyInQueue && wasInQueue) {
      // Left queue, reset tracking
      setQueueStartTime(null);
      setQueueFallbackTriggered(false);
    }
  }, [inQueue, selectedInvitationId, queueStartTime, queueFallbackTriggered]);

  // Handle queue fallback timeout
  useEffect(() => {
    if (!queueStartTime || queueFallbackTriggered || inQueue !== selectedInvitationId) {
      return;
    }

    const timeoutId = setTimeout(() => {
      console.log('Queue fallback triggered after timeout, re-launching with region auto');
      setQueueFallbackTriggered(true);
      onStart?.(true, 'auto');
    }, QUEUE_FALLBACK_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [queueStartTime, queueFallbackTriggered, inQueue, selectedInvitationId, onStart, QUEUE_FALLBACK_TIMEOUT]);

  useEffect(() => {
    if (selectedInvitationId) {
      setShowInviteFriends(false);
    }
  }, [selectedInvitationId]);

  const selectedInvitation = invitations.find((inv) => inv.id === selectedInvitationId);

  // Narrow type of invitation to "cafe" only.
  const cafeInvitation = selectedInvitation as Invitation & { type: 'cafe' };
  const players = useMemo(() => cafeInvitation?.manifest?.players || [], [cafeInvitation?.manifest?.players]);
  const isOwner = cafeInvitation?.owner.profileId === authId;
  const isPlayerInLobby = players.some((player) => player.playerId === authId);

  // Determine if the user is the owner but not in the lobby (left previously)
  const isOwnerWhoLeft = isOwner && !isPlayerInLobby;

  // The user should see owner controls if they're the owner AND they're in the lobby
  const shouldShowOwnerControls = isOwner && isPlayerInLobby;

  const queueStatus = cafeInvitation?.manifest?.queueStatus;
  const queueMessage = cafeInvitation?.manifest?.queueMessage;

  // Track player changes to detect when a player is added
  useEffect(() => {
    if (!players || !authId || !selectedInvitationId) return;

    const wasPlayerInLobby = previousPlayersRef.current.some((p) => p.playerId === authId);
    const isPlayerInLobbyNow = players.some((p) => p.playerId === authId);
    const isReady = queueStatus === 'ready';

    if (joiningMatchedBroadcast && !wasPlayerInLobby && isPlayerInLobbyNow && isReady) {
      // Player has been added to the broadcast, but we'll let user click the link manually
      // window.open(`${constants.PLAYER_BASE_URL}j/${selectedInvitationId}`, '_blank');
      // Removed setLastLaunchTime(Date.now()) to prevent unwanted cooldown
      setJoiningMatchedBroadcast(false);
    }

    // Update the ref (doesn't trigger re-render)
    previousPlayersRef.current = players;
  }, [joiningMatchedBroadcast, players, authId, selectedInvitationId, queueStatus]);

  // Get status text that appears above the button
  const getStatusText = (isOwner: boolean) => {
    if (isOwner) {
      // If we have a queue status
      switch (queueStatus) {
        case 'failed':    
        case 'cancelled':
          return 'Failed';
        case 'queued':
          return 'Searching...';
        case 'matched':
          return 'Setting Up';
        case 'ready':     
          return 'Match Found!';
      }

      // If we are in queue, show searching.
      if (inQueue === selectedInvitationId) return 'Searching...';
      // If a match has been found but not ready... could probably get rid of this.
      if (matchFoundId === selectedInvitationId) return 'Match Found!';
      if (matchBroadcastId === selectedInvitationId) return 'Opening Broadcast...';
    } else {

      // If Owner left and the their own broadcast is full.
      if (isOwnerWhoLeft) {
        if (players.length >= 4) return 'Broadcast Full';
        return ''; // Hide "Rejoin" status text
      }

      // If we have a queue status
      switch (queueStatus) {
        case 'failed':
        case 'cancelled': 
          return 'Failed';
        case 'queued':
          return 'Searching...';
        case 'matched':
          return 'Setting Up';
        case 'ready': {
          if (isPlayerInLobby) {
            return matchFoundId === selectedInvitationId ? 'Match Found!' : 'Joined';
          }
          return 'Joined';
        }
      }

      // Special match found/ready text.
      if (inQueue === selectedInvitationId) return queueFallbackTriggered ? 'Searching...' : 'Joining...';
      if (matchFoundId === selectedInvitationId) return 'Match Found!';
      if (matchBroadcastId === selectedInvitationId) return 'Opening Broadcast...';

      // Broadcast full.
      if (players.length >= 4) return 'Broadcast Full';

      // If the join button is pressed.
      if (joinButtonClicked) return 'Joining...';
    }

    // No status text for idle state (Join Lobby)
    return ''; 
  };

  // Get complete button state (text + opacity) for both launch and join buttons
  const getButtonState = (isOwner: boolean, isPlayerInLobby: boolean) => {
    const isMatchReady = matchFoundId === selectedInvitationId && queueStatus === 'ready';
    const isDisabled = isButtonDisabled();

    // Special case: Broadcast Full, always 60 opacity
    if (players.length >= 4) {
      return {
        text: 'Broadcast Full',
        opacity: 'opacity-60'
      };
    }

    // Check for match found - only show "Play!" for players already in lobby
    if (isMatchReady) {
      // Owner who left should still see "Rejoin" even when match is found
      if (isOwner && isOwnerWhoLeft) {
        return {
          text: 'Rejoin',
          opacity: isDisabled ? 'opacity-60' : 'opacity-100'
        };
      }
      // Only show "Play!" for players who are already in the lobby
      if (isPlayerInLobby) {
        return {
          text: 'Play!',
          opacity: isDisabled ? 'opacity-60' : 'opacity-100'
        };
      }
      // For players not in lobby yet, show "Join Lobby" even when match is found
      return {
        text: 'Join Lobby',
        opacity: isDisabled ? 'opacity-60' : 'opacity-100'
      };
    }

    // Owner flow
    if (isOwner && isPlayerInLobby) {
      //console.log('MATCH READY! wtfffff', isDisabled);
      return {
        text: 'Launch',
        opacity: isDisabled ? 'opacity-60' : 'opacity-100'
      };
    }

    // Player flow
    if (isOwnerWhoLeft) {
      return {
        text: 'Rejoin',
        opacity: isDisabled ? 'opacity-60' : 'opacity-100'
      };
    }

    if (isPlayerInLobby) {
      return {
        text: 'Waiting on Lobby Owner',
        opacity: 'opacity-60'
      };
    }

    return {
      text: 'Join Lobby',
      opacity: isDisabled ? 'opacity-60' : 'opacity-100'
    };
  };

  // Turn off instant feedback when we find the match.
  useEffect(() => {
    const matchReady = matchFoundId === selectedInvitationId;
    if (matchReady) setLaunchButtonClicked(false);
  }, [selectedInvitationId, matchFoundId, queueStatus]);

  const isButtonDisabled = () => {
    // If in a failed state.
    if (queueStatus === 'failed' || queueStatus === 'cancelled') return true;

    // If the match has been found and we are ready to connect.
    const matchReady = matchFoundId === selectedInvitationId && queueStatus === 'ready';

    // Other blockers.
    if (joiningMatchedBroadcast) return true;
    if (matchReady && cooldownActive) return true;
    if (players.length >= 4) return true;
    if (launchButtonClicked) return true;

    // If the match is found but the machine is not ready.
    if (selectedInvitationId === matchFoundId && !matchReady) return true;

    return false;
  };

  const getBroadcastUrl = () => {
    return `${constants.PAVILION_PLAYER_BASE_URL}j/${selectedInvitationId}`;
  };

  const handleButtonClick = (isLaunch: boolean) => {
    console.log('handleButtonClick', isLaunch);
    console.log('cooldownActive', cooldownActive);
    console.log('joiningMatchedBroadcast', joiningMatchedBroadcast);
    if (cooldownActive || joiningMatchedBroadcast) return;

    const matchReady = matchFoundId === selectedInvitationId && queueStatus === 'ready'
    if (!matchReady)
      setLaunchButtonClicked(true);

    if (isLaunch) {
      sendAnalytics({
        name: 'Broadcast Launched',
        role: 'host',
        persistentId: persistentId,
        hostAuthId: authId,
        action: 'launch button',
        lobbyId: selectedInvitationId,
        playersAmount: players.length,
        source: parentType,
      } as PlaycastAnalyticsBroadcastLaunched);

      if ((matchFoundId === selectedInvitationId && queueStatus !== 'failed') || queueStatus === 'ready') {
        // Owner can directly use the hyperlink (the a tag handles this now)
        setLastLaunchTime(Date.now());
      } else {
        // Only show login dialog if their login preference is not set
        if (!selectedInvitationId) return;
        // if (!loginPreferences[selectedInvitationId]) {
        //   setShowLoginDialog(true);
        // } else {
        //   onStart?.(loginPreferences[selectedInvitationId]);
        // }
        onStart?.(true);
      }
    } else {
      // Set the clicked state immediately for immediate UI feedback
      setJoinButtonClicked(true);
      
      const matchReady = matchFoundId === selectedInvitationId || queueStatus === 'matched' || queueStatus === 'ready';

      if (!isPlayerInLobby && matchReady) {
        // Player needs to join match first, then we'll show the hyperlink
        setJoiningMatchedBroadcast(true);
        onJoin?.(playerName);
        // The hyperlink will be visible once they're added to the lobby
      } else if (isPlayerInLobby && (matchFoundId === selectedInvitationId || queueStatus === 'ready')) {
        // Player is already in the lobby and match is ready (the a tag handles this now)
        setLastLaunchTime(Date.now());
      } else {
        // Normal join flow
        onJoin?.(playerName);
      }
    }
  };

  // const handleStartGame = (useSteamGuestLogin: boolean) => {
  //   if (selectedInvitationId) {
  //     setLoginPreferences((prev) => ({
  //       ...prev,
  //       [selectedInvitationId]: useSteamGuestLogin,
  //     }));
  //   }

  //   setShowLoginDialog(false);

  //   onStart?.(useSteamGuestLogin);
  // };

  // const handleOnClose = () => {
  //   setShowConfirmClose(!showConfirmClose);
  // };

  const handleRemoveSelectedInvitation = () => {
    onRemoveSelectedInvitation();
    setShowLauncher(false);
  };

  const handleCopyLink = () => {
    sendAnalytics({
      name: 'Invite Created',
      role: 'host',
      inviteType: 'copy link',
      hostAuthId: authId,
      invitationId: selectedInvitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsInviteCreated);

    const playerLink = constants.PAVILION_PLAYER_BASE_URL;
    navigator.clipboard.writeText(`${playerLink}j/${selectedInvitationId}`);
    toast.success('Link copied to clipboard');
    setShowInviteFriends(false);
  };

  const handleKickPlayer = (playerId: string) => {
    onKickPlayer && onKickPlayer(playerId);
  };

  const handleDiscordBotClick = () => {
    if (!selectedInvitationId) return;
    console.log('Discord Bot');

    sendAnalytics({
      name: 'Invite Created',
      role: 'host',
      inviteType: 'discord bot',
      hostAuthId: authId,
      invitationId: selectedInvitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsInviteCreated);
    handleDiscordBot(selectedInvitationId);
  };

  const handleTwitchBotClick = () => {
    if (!selectedInvitationId) return;
    console.log('Twitch Bot');

    sendAnalytics({
      name: 'Invite Created',
      role: 'host',
      inviteType: 'twitch bot',
      hostAuthId: authId,
      invitationId: selectedInvitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsInviteCreated);
    handleTwitchBot(selectedInvitationId);
  };

  const handleUpdatePlayerName = (name: string) => {
    console.log('LauncherModal: handleUpdatePlayerName called with:', name);
    setPlayerName(name);
    // Save to localStorage immediately
    localStorage.setItem('name', name);
    console.log('LauncherModal: Saved to localStorage:', name);
    // Update the global state
    onUpdatePlayerName(name);
    console.log('LauncherModal: Called onUpdatePlayerName with:', name);
  };

  useEffect(() => {
    if (isVisible !== undefined) {
      setShowLauncher(isVisible);
    }
  }, [isVisible]);

  // Reset showLauncher when selectedInvitationId changes
  useEffect(() => {
    if (selectedInvitationId) {
      setShowLauncher(true);
      // Reset button states when opening a new lobby
      setJoinButtonClicked(false);
      setJoiningMatchedBroadcast(false);
      setQueueFallbackTriggered(false);
      setQueueStartTime(null);
    }
  }, [selectedInvitationId]);

  // Mobile detection
  const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isMobileLandscape = isMobile && window.innerWidth > window.innerHeight;

  // Single effect to handle modal positioning
  useEffect(() => {
    if (!showLauncher || !modalRef.current) return;

    let resizeObserver: ResizeObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const positionModal = () => {
      if (!modalRef.current) return;

      const modalWidth = modalRef.current.offsetWidth;
      const modalHeight = modalRef.current.offsetHeight;

      // Only position if we have valid dimensions
      if (modalWidth > 0 && modalHeight > 0) {
        let x: number;
        let y: number;

        // Check if mobile portrait (not landscape)
        const isMobilePortrait = isMobile && !isMobileLandscape;

        if (isMobilePortrait) {
          // Mobile portrait: center bottom
          x = (window.innerWidth - modalWidth) / 2;
          y = window.innerHeight - modalHeight - 20;
        } else if (initialPosition) {
          // Position relative to the card
          x = initialPosition.x;
          y = initialPosition.y - modalHeight / 2;
        } else {
          // Center in the viewport (desktop/landscape)
          x = Math.max(0, (window.innerWidth - modalWidth) / 2);
          y = Math.max(0, (window.innerHeight - modalHeight) / 2);
        }

        // Clamp to viewport bounds
        x = Math.min(Math.max(0, x), window.innerWidth - modalWidth);
        y = Math.min(Math.max(0, y), window.innerHeight - modalHeight);

        setModalPosition({ x, y });

        // Clean up observer once positioned
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
      }
    };

    // Try to position immediately
    positionModal();

    // If dimensions aren't available yet, set up a ResizeObserver
    if (modalRef.current.offsetWidth === 0 || modalRef.current.offsetHeight === 0) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
            // Clear any pending timeout
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            positionModal();
          }
        }
      });

      resizeObserver.observe(modalRef.current);

      // Fallback timeout in case ResizeObserver doesn't fire
      timeoutId = setTimeout(() => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
        positionModal();
      }, 200);
    }

    return (): void => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showLauncher, initialPosition, isMobile, isMobileLandscape]);

  if (!selectedInvitationId || !selectedInvitation || !showLauncher) return null;

  const doesButtonHyperlink = () => {
    const matchReady = matchFoundId === selectedInvitationId && queueStatus === 'ready';
    return isPlayerInLobby && matchReady;
  };

  // Create button or anchor based on condition
  const renderButton = (isOwner: boolean, isPlayerInLobby: boolean) => {
    // Does the button send the user to the broadcast page.
    const useHyperlink = doesButtonHyperlink();

    // Is the button clickable.
    const isDisabled = isButtonDisabled();

    // What is displayed on the button.
    const buttonState = getButtonState(isOwner, isPlayerInLobby);

    // The text that is displayed above the button.
    const statusText = getStatusText(isOwner);

    const buttonContent = (
      <div className='flex items-center gap-2'>
        <span>{buttonState.text}</span>
      </div>
    );

    // This is gross.
    if (useHyperlink) {
      return (
        <div className='flex flex-col gap-2'>
          {statusText && (
            <div className='text-center text-sm text-amber-200'>
              {statusText}
            </div>
          )}
          <a
            href={getBroadcastUrl()}
            target='_blank'
            rel='noopener noreferrer'
            className='relative'
            data-testid={isOwner ? 'launch-broadcast-link' : 'join-broadcast-link'}
          >
            <span
              className='absolute inset-0 z-10 flex -translate-y-0.5 items-center justify-center text-2xl font-bold'
              style={{
                color: '#AE7B00',
                WebkitTextStrokeWidth: '1px',
                WebkitTextStrokeColor: '#966D09',
              }}
            >
              {buttonContent}
            </span>
            <img src='/launch-button.svg' alt='launch' className='w-full' />
          </a>
        </div>
      );
    } else {
      return (
        <div className='flex flex-col gap-2'>
          {statusText && (
            <div className='text-center text-sm text-amber-200'>
              {statusText}
            </div>
          )}
          <button
            className={`relative w-full ${buttonState.opacity}`}
            onClick={() => handleButtonClick(isOwner && isPlayerInLobby)}
            disabled={isDisabled}
            data-testid={isOwner && isPlayerInLobby ? 'launch-broadcast-button' : 'join-broadcast-button'}
          >
            <span
              className='absolute inset-0 z-10 flex -translate-y-0.5 items-center justify-center text-2xl font-bold w-full'
              style={{
                color: '#AE7B00',
                WebkitTextStrokeWidth: '1px',
                WebkitTextStrokeColor: '#966D09',
              }}
            >
              {buttonContent}
            </span>
            <img src='/launch-button.svg' alt='launch' className='w-full' />
          </button>
        </div>
      );
    }
  };

  return (
    <>
      <div
        ref={modalRef}
        className={clsx(
          'fixed inset-0 z-[99999999] flex w-full max-w-sm flex-col rounded-[20px] bg-black bg-[linear-gradient(180deg,_rgba(25,38,78,0)_0%,_#3B0764_63.46%)] p-4 md:inset-4 md:rounded-[20px]',
          isMobile ? (isMobileLandscape ? 'max-w-sm' : 'h-2/3 max-w-sm') : 'max-h-screen lg:h-2/3',
        )}
        style={{
          transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='absolute inset-0 z-10 select-none rounded-[20px] border-[6px] border-amber-400 blur-[2px]' />
        <div className='z-20 flex h-full flex-col'>
          <div
            className='relative flex cursor-grab justify-center px-2 active:cursor-grabbing'
            id='launcher-header-drag-area'
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <img src='/icon.png' alt='PC Logo' className='pointer-events-none size-12 select-none lg:size-20' />
            <button onClick={handleRemoveSelectedInvitation}>
              <FontAwesomeIcon
                icon={faArrowDownFromLine}
                className='absolute right-2 top-1/2 size-8 -translate-y-1/2 text-amber-400 lg:size-12'
              />
            </button>
          </div>
          <div className='flex flex-1 flex-col gap-2 p-2 min-h-0'>
            {/* Show queue failed feedback if needed */}
            {queueStatus === 'failed' && (
              <div className='mb-2 flex flex-col items-center gap-2 rounded-lg border-2 border-red-400 bg-red-900/80 p-2 text-red-200'>
                <span className='font-bold'>Matchmaking failed.</span>
                <span className='text-sm'>{queueMessage}</span>
                {shouldShowOwnerControls && (
                  <button
                    className='text-darkest rounded-lg bg-amber-400 px-4 py-2 font-bold hover:bg-amber-400/80'
                    onClick={() => handleButtonClick(true)}
                    disabled={false}
                    data-testid='retry-matchmaking-button'
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            {/* Show queue fallback message if triggered */}
            {queueFallbackTriggered && (inQueue === selectedInvitationId || queueStatus === 'queued') && (
              <div className='mb-2 flex flex-col items-center gap-2 rounded-lg border-2 border-amber-400 bg-amber-900/80 p-2 text-amber-200'>
                <span className='font-bold'>Searching wider for matches...</span>
                <span className='text-sm'>Expanding search to all regions for better match</span>
              </div>
            )}
            {/* Combined scrollable container for invite modal and player list */}
            <div className='flex-1 min-h-0 overflow-y-auto'>
              <div className='flex flex-col gap-2'>
                {showInviteFriends ? (
                  <div className='border-primary bg-darker relative flex flex-col items-center gap-4 rounded-lg border-2 px-10 py-2 text-center'>
                    <button
                      className='absolute right-2 top-2'
                      onClick={() => setShowInviteFriends(false)}
                      data-testid='close-invite-panel-button'
                    >
                      <FontAwesomeIcon icon={faXmark} className='size-6 text-white' />
                    </button>
                    <span className='text-xl font-bold text-purple-500'>Invite</span>
                    <div className='flex w-full flex-col gap-2'>
                      <InviteButton
                        icon={faUserPlus}
                        text='Share'
                        invitationId={selectedInvitationId}
                        onCopy={handleCopyLink}
                        onSendEmail={handleSendEmail}
                        onSendSms={handleSendSms}
                        invitationSendState={invitationSendState}
                        authId={authId}
                      />

                      <div className='relative'>
                        <button
                          onClick={handleDiscordBotClick}
                          className={`border-primary flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 p-2 font-bold ${ownerAliases?.discord ? 'hover:border-primary/80 text-white' : 'cursor-not-allowed text-gray-400 opacity-50'
                            }`}
                          disabled={!ownerAliases?.discord}
                          title={!ownerAliases?.discord ? 'Discord account not linked' : undefined}
                          data-testid='discord-bot-button'
                        >
                          <FontAwesomeIcon icon={faDiscord} className={`${ownerAliases?.discord ? 'text-[#5865f2]' : 'text-gray-400'}`} />
                          <span>Discord Bot</span>
                        </button>
                        {/* add a button to redirect to pavilion.playcast.io/faqs */}
                        <button
                          className='absolute -right-8 top-1/2 -translate-y-1/2'
                          onClick={() => window.open('https://pavilion.playcast.io/faqs', '_blank')}
                        >
                          <img src='/question-pav.svg' alt='Question' className='size-6' />
                        </button>
                      </div>
                      <div className='relative'>
                        <button
                          onClick={handleTwitchBotClick}
                          className={`border-primary flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 p-2 font-bold ${ownerAliases?.twitch ? 'hover:border-primary/80 text-white' : 'cursor-not-allowed text-gray-400 opacity-50'
                            }`}
                          disabled={!ownerAliases?.twitch}
                          title={!ownerAliases?.twitch ? 'Twitch account not linked' : undefined}
                          data-testid='twitch-bot-button'
                        >
                          <FontAwesomeIcon icon={faTwitch} className={`${ownerAliases?.twitch ? 'text-[#9146ff]' : 'text-gray-400'}`} />
                          <span>Twitch Bot</span>
                        </button>
                        <button
                          className='absolute -right-8 top-1/2 -translate-y-1/2'
                          onClick={() => window.open('https://pavilion.playcast.io/faqs', '_blank')}
                        >
                          <img src='/question-pav.svg' alt='Question' className='size-6' />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  shouldShowOwnerControls && (
                    <button
                      className='flex items-center gap-2 rounded-lg border-2 border-amber-200 p-2'
                      onClick={() => {
                        sendAnalytics({
                          name: 'Invite Friends Initialized',
                          role: 'host',
                          persistentId: persistentId,
                          hostAuthId: authId,
                          action: 'invite friends button'
                        } as PlaycastAnalyticsInviteFriendsInitialized);
                        setShowInviteFriends(true);
                      }}
                      data-testid='invite-friends-button'
                    >
                      <FontAwesomeIcon icon={faUserPlus} className='size-4 text-amber-200 lg:size-6' />
                      <span className='text-xs font-semibold text-amber-200 lg:text-base'>Invite Your Friends To Join</span>
                    </button>
                  )
                )}
                {/* Player list - now part of the scrollable container */}
                {players.map((player, idx) => (
                  <div
                    key={player.playerId ?? `player-${idx}`}
                    className='border-primary bg-primary flex items-center gap-1 rounded-lg border-2'
                  >
                    <div className='bg-darker flex w-full items-center overflow-hidden rounded-md p-2 font-semibold'>
                      <span className='overflow-hidden text-ellipsis text-xs lg:text-base'>{player.displayName || `Player ${idx + 1}`}</span>
                      {authId === player.playerId && <span className='whitespace-pre text-xs lg:text-base'> (You)</span>}
                    </div>
                    {shouldShowOwnerControls && player.playerId !== authId && (
                      <button
                        className='flex-none rounded-md bg-orange-600/50 p-2 hover:bg-orange-600/40'
                        onClick={() => player.playerId && handleKickPlayer(player.playerId)}
                        data-testid={`kick-player-${player.playerId}-button`}
                      >
                        <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600' />
                      </button>
                    )}
                  </div>
                ))}
                {!isSignedIn && !isPlayerInLobby && (
                  <div className='border-primary bg-primary flex items-center gap-1 rounded-lg border-2'>
                    <div className='bg-darker flex w-full items-center overflow-hidden rounded-md p-2 font-semibold'>
                      <Input
                        placeholder='Anonymous (You)'
                        className='w-full rounded-sm border-gray-500 bg-transparent'
                        value={playerName}
                        onChange={(e) => handleUpdatePlayerName(e.target.value)}
                        data-testid='anon-player-name-input'
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Button container - always at bottom */}
            <div className='mt-2 flex-shrink-0'>
              {renderButton(isOwner, isPlayerInLobby)}
            </div>
          </div>
        </div>
      </div>
      {/* {showConfirmClose && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-black/50' onClick={handleOnClose}>
          <div className='bg-darkest flex flex-col gap-4 rounded-lg p-4'>
            <span className='text-white'>Are you sure you want to close the lobby?</span>
            <div className='flex justify-center gap-2'>
              <button
                className='rounded-lg bg-red-600 p-2 hover:bg-red-600/80 active:bg-red-600/60'
                onClick={handleConfirmClose}
                data-testid='confirm-close-button'
              >
                Confirm
              </button>
              <button
                className='bg-primary hover:bg-primary/80 active:bg-primary/60 rounded-lg p-2'
                onClick={handleOnClose}
                data-testid='cancel-close-button'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )} */}
      {/* {showLoginDialog && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-black/50' onClick={() => setShowLoginDialog(false)}>
          <div
            className='bg-darkest flex w-full max-w-lg flex-col gap-2 rounded-lg p-8 text-center text-white'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='text-xl font-bold'>How would you like to sign in for this session?</h3>
            <span>This helps us get you into the right environment</span>
            <div className='mt-8 flex flex-col gap-2 sm:flex-row'>
              <button
                className='border-darkest flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 p-2 hover:border-amber-400'
                onClick={() => handleStartGame(false)}
                data-testid='steam-login-button'
              >
                <img src='/steam-logo.png' alt='Steam' className='size-32' />
                <span>Use my Steam Account</span>
              </button>
              <button
                className='border-darkest flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 p-2 hover:border-amber-400'
                onClick={() => handleStartGame(true)}
                data-testid='guest-login-button'
              >
                <img src='/pav-logo.png' alt='Playcast' className='size-32' />
                <span className='text-sm'>Use a Playcast Guest Account</span>
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};
